import {cva} from 'class-variance-authority';

export const buttonVariants = cva(
  [
    'button relative inline-flex items-center justify-center gap-2 whitespace-nowrap',
    '[font-family:var(--button-font-family)] [font-weight:var(--button-font-weight)]',
    'transition-all duration-150 ease-out outline-none',
    'focus-visible:[box-shadow:var(--button-focus-shadow)]',
    'disabled:pointer-events-none disabled:[opacity:var(--button-disabled-opacity)]',
    '[&_svg]:pointer-events-none [&_svg]:size-[var(--button-icon-size)] [&_svg]:shrink-0',
    'before:absolute before:inset-0 before:rounded-[inherit] before:content-[""]',
    'before:bg-transparent before:transition-colors before:duration-150',
    '[&>*]:relative [&>*]:z-[1]',
  ],
  {
    variants: {
      variant: {
        default: [
          'button_default',
          'bg-[var(--button-primary-bg)] text-[color:var(--button-primary-text)]',
          'hover:before:bg-[var(--button-primary-hover-overlay)] active:before:bg-[var(--button-primary-active-overlay)]',
          'disabled:hover:before:bg-transparent',
          'disabled:bg-[var(--button-primary-disabled-bg)] disabled:text-[color:var(--button-primary-disabled-text)] disabled:[opacity:var(--button-primary-disabled-opacity)]',
        ],
        primary: [
          'button_default',
          'bg-[var(--button-primary-bg)] text-[color:var(--button-primary-text)]',
          'hover:before:bg-[var(--button-primary-hover-overlay)] active:before:bg-[var(--button-primary-active-overlay)]',
          'disabled:hover:before:bg-transparent',
          'disabled:bg-[var(--button-primary-disabled-bg)] disabled:text-[color:var(--button-primary-disabled-text)] disabled:[opacity:var(--button-primary-disabled-opacity)]',
        ],
        destructive: [
          'button_destructive',
          'border border-[var(--button-destructive-border)] bg-[var(--button-destructive-bg)] text-[color:var(--button-destructive-text)]',
          'shadow-[var(--button-shadow)]',
          'hover:bg-[var(--button-destructive-hover-bg)]',
          'disabled:hover:bg-[var(--button-destructive-bg)]',
          'disabled:bg-[var(--button-destructive-disabled-bg)] disabled:text-[color:var(--button-destructive-disabled-text)]',
        ],
        secondary: [
          'button_secondary',
          'border border-[var(--button-secondary-border)] bg-[var(--button-secondary-bg)] text-[color:var(--button-secondary-text)]',
          'shadow-[var(--button-shadow)]',
          'hover:before:bg-[var(--button-secondary-hover-overlay)] active:before:bg-[var(--button-secondary-active-overlay)]',
          'disabled:hover:before:bg-transparent',
          'disabled:bg-[var(--button-secondary-disabled-bg)] disabled:text-[color:var(--button-secondary-disabled-text)]',
        ],
        ghost: [
          'button_ghost',
          'bg-[var(--button-ghost-bg)] text-[color:var(--button-ghost-text)]',
          'hover:bg-[var(--button-ghost-hover-bg)] active:bg-[var(--button-ghost-active-bg)]',
          'disabled:hover:bg-[var(--button-ghost-bg)]',
          'disabled:bg-[var(--button-ghost-disabled-bg)] disabled:text-[color:var(--button-ghost-disabled-text)]',
        ],
        link: [
          'button_link',
          'h-auto [font-weight:var(--button-link-font-weight)] min-w-0 rounded-none bg-[var(--button-link-bg)] px-0 py-0 text-[color:var(--button-link-text)]',
          'hover:opacity-80 active:opacity-80 before:hidden',
          'disabled:hover:no-opacity-80',
          'disabled:text-[color:var(--button-link-disabled-text)]',
        ],
        tertiary: [
          'button_tertiary',
          'border border-[var(--button-tertiary-border)] bg-[var(--button-tertiary-bg)] text-[color:var(--button-tertiary-text)]',
          'shadow-[var(--button-shadow)]',
          'hover:before:bg-[var(--button-tertiary-hover-overlay)] active:before:bg-[var(--button-tertiary-active-overlay)]',
          'disabled:hover:before:bg-transparent',
          'disabled:bg-[var(--button-tertiary-disabled-bg)] disabled:text-[color:var(--button-tertiary-disabled-text)]',
        ],
        outline: [
          'border border-[var(--button-outline-border)] bg-[var(--button-outline-bg)] text-[color:var(--button-outline-text)]',
          'hover:bg-[var(--button-outline-hover-bg)]',
          'disabled:hover:bg-[var(--button-outline-bg)]',
          'disabled:bg-[var(--button-outline-disabled-bg)] disabled:text-[color:var(--button-outline-disabled-text)]',
        ],
      },
      size: {
        default:
          'button_size_default h-[var(--button-height-default)] min-w-[var(--button-min-width-default)] px-4 py-2 text-[length:var(--button-font-size)]',
        m: 'button_size_m h-[var(--button-height-m)] min-w-[var(--button-min-width-m)] px-4 text-[length:var(--button-font-size)]',
        md: 'button_size_m h-[var(--button-height-m)] min-w-[var(--button-min-width-m)] px-4 text-[length:var(--button-font-size)]',
        s: 'button_size_sm h-[var(--button-height-s)] px-[12px] text-[13px]',
        sm: 'button_size_sm h-[var(--button-height-sm)] px-3 text-[length:var(--button-font-size)]',
        lg: 'button_size_lg h-[var(--button-height-lg)] px-8 text-[15px]',
        xs: 'button_size_xs h-[var(--button-height-xs)] px-[10px] text-[12px]',
        icon: 'button_size_icon h-[var(--button-height-icon)] w-[var(--button-height-icon)] p-0',
      },
      shape: {
        rounded: 'rounded-[var(--button-radius)]',
        pill: 'rounded-full',
      },
      readOnly: {
        true: 'button_readonly pointer-events-none cursor-not-allowed !opacity-100',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'link',
        className: 'h-auto min-w-0 px-0 py-0 rounded-none',
      },
      {
        size: 'icon',
        shape: 'rounded',
        className: 'rounded-md',
      },
      {
        size: 'icon',
        shape: 'pill',
        className: 'rounded-full',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      shape: 'rounded',
      readOnly: false,
    },
  },
);
